import type { Experiment } from '@/api/experiment/experimentType';
import { del, get, getStoredAccessToken, post, refreshAccessToken } from '@/api/request.ts';
import { connect } from '@/utils/sse';

export async function fetchFavoriteExperimentsApi() {
  const result = await get<{
    experiments: Experiment[];
  }>(`/experiment/favorite/`);
  return result.data;
}

export async function toggleFavoriteApi(expId: string) {
  const result = await post<{
    isFavorite: boolean;
  }>(`/experiment/${expId}/favorite/`);
  return result.data;
}

export async function fetchExperimentTagsApi(expId: string) {
  const result = await get<{
    tags: Array<{ id: number; name: string }>;
  }>(`/experiment/experiment/${encodeURIComponent(expId)}/tags/`);
  return result.data.tags;
}

// 实验部分ai对话相关接口
export interface Chat {
  id: string;
  role: string;
  content: string;
  createdAt: string;
  status: string;
  hasReceivedText?: boolean;
  isUsingTool?: boolean;
}

function buildExperimentChatHeaders(token: string | null): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': '*/*',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function getErrorStatus(err: unknown): number | undefined {
  if (!err || typeof err !== 'object' || !('status' in err)) {
    return undefined;
  }

  const status = Number((err as { status?: unknown }).status);
  return Number.isFinite(status) ? status : undefined;
}

export async function fetchExperimentChatHistoryApi(expId: string, mode: 'normal' | 'deep' = 'normal') {
  const result = await get<{
    conversation: {
      id: string;
      messages: Chat[];
      createdAt: string;
      updatedAt: string;
      mode: 'normal' | 'deep';
      topic: string;
    } | null;
  }>(`/experiment/chat/${expId}/`, { mode });
  return result.data;
}

export async function clearExperimentChatsApi(expId: string, mode: 'normal' | 'deep' = 'normal') {
  const result = await del<null>(`/experiment/chat/${expId}/`, { mode });
  return result.data;
}

export async function saveExperimentDeepTopicApi(expId: string, topic: string) {
  const result = await post<{
    topic: string;
    conversationId: string;
  }>(`/experiment/chat/${expId}/topic/`, {
    mode: 'deep',
    topic,
  });
  return result.data;
}

export async function chatWithAiApi(
  expId: string,
  content: string,
  source: string,
  conversationId: string | null,
  context: string | null,
  mode: 'normal' | 'deep' = 'normal',
  topic: string | null = null,
  onopen: (response: Response) => Promise<void>,
  onmessage: (data: any) => void,
  onclose: () => void,
  onerror: (ev: Event) => void,
  controller: AbortController,
) {
  const connectWithToken = async (token: string | null) => {
    await connect({
      url: `/experiment/chat/${expId}/`,
      method: 'POST',
      body: {
        content,
        source,
        mode,
        ...(conversationId ? { conversationId } : {}),
        ...(context ? { context } : {}),
        ...(topic ? { topic } : {}),
      },
      headers: buildExperimentChatHeaders(token),
      controller,
      onopen,
      onmessage: (ev) => {
        try {
          const data = JSON.parse(ev.data);
          onmessage(data);
        }
        catch (e) {
          console.error('Error parsing SSE message:', e);
        }
      },
      onclose,
      onerror,
    });
  };

  try {
    await connectWithToken(getStoredAccessToken());
  }
  catch (streamError) {
    if (controller.signal.aborted || getErrorStatus(streamError) !== 401) {
      throw streamError;
    }

    const refreshedToken = await refreshAccessToken();
    if (controller.signal.aborted) {
      return;
    }
    await connectWithToken(refreshedToken);
  }
}
